/**
 * Catalogue modal state and actions, ported from loader-catalog.js. Catalogue
 * satellites live in the loader dataset as tier 9 missions with isCatalog set,
 * so the globe, the strip and the analysis see them like any other mission.
 */
import { create } from 'zustand';
import { toast } from '@/components/ui/toast';
import { useLoader } from '../store';
import type { Mission } from '../types';
import {
  CATALOG_PALETTE,
  catalogMissionFromOmm,
  catalogMissionId,
  loadGroup as loadGroupRecords,
  ommName,
  ommNoradId,
  searchByName,
  type OmmRecord,
} from './celestrak';

/** Search runs this long after the last keystroke (as the original). */
export const SEARCH_DEBOUNCE_MS = 400;
/** Queries shorter than this are not sent (as the original). */
export const MIN_QUERY_LENGTH = 2;
/** "Add all shown" stops here so one click cannot push the globe into bulk mode by accident. */
export const MAX_ADD_AT_ONCE = 200;

export type CatalogStatus = 'idle' | 'loading' | 'ready' | 'error';

interface CatalogState {
  open: boolean;
  query: string;
  group: string;
  status: CatalogStatus;
  statusText: string;
  results: OmmRecord[];
  /** When the current results were loaded; the rows' epoch ages are relative to it. */
  loadedAtMs: number;
  requestSeq: number;

  openCatalog: () => void;
  closeCatalog: () => void;
  setOpen: (open: boolean) => void;
  setQuery: (query: string) => void;
  setGroup: (group: string) => void;
  search: (query: string) => Promise<void>;
  loadGroup: (group: string) => Promise<void>;
  /** Adds the records to the globe (skipping those already there); returns how many were added. */
  addToGlobe: (records: OmmRecord[]) => number;
  removeFromGlobe: (missionIds: string[]) => void;
  removeAll: () => void;
}

let colorIndex = 0;
function nextColor(): string {
  const color = CATALOG_PALETTE[colorIndex % CATALOG_PALETTE.length];
  colorIndex++;
  return color;
}

/** Missions currently on the globe that came from the catalogue. */
export function selectCatalogMissions(missions: Mission[] | undefined): Mission[] {
  return (missions ?? []).filter((m) => m.isCatalog === true);
}

export const useCatalog = create<CatalogState>()((set, get) => ({
  open: false,
  query: '',
  group: '',
  status: 'idle',
  statusText: 'No results loaded',
  results: [],
  loadedAtMs: 0,
  requestSeq: 0,

  openCatalog: () => set({ open: true }),
  closeCatalog: () => set({ open: false }),
  setOpen: (open) => set({ open }),
  setQuery: (query) => set({ query }),
  setGroup: (group) => set({ group }),

  search: async (query) => {
    const seq = get().requestSeq + 1;
    set({ requestSeq: seq, status: 'loading', statusText: 'Searching...' });
    try {
      const records = await searchByName(query);
      if (get().requestSeq !== seq) return;
      set({
        status: 'ready',
        statusText: `${records.length} results for "${query}"`,
        results: records,
        loadedAtMs: Date.now(),
      });
    } catch (err: unknown) {
      if (get().requestSeq !== seq) return;
      const message = err instanceof Error ? err.message : String(err);
      console.error('[catalog] search failed:', err);
      set({ status: 'error', statusText: 'Search failed. Check connection.' });
      toast('error', `Satellite search failed: ${message}`);
    }
  },

  loadGroup: async (group) => {
    const seq = get().requestSeq + 1;
    set({ requestSeq: seq, status: 'loading', statusText: 'Loading group...' });
    try {
      const { records } = await loadGroupRecords(group);
      if (get().requestSeq !== seq) return;
      set({
        status: 'ready',
        statusText: `${records.length} satellites in group`,
        results: records,
        loadedAtMs: Date.now(),
      });
    } catch (err: unknown) {
      if (get().requestSeq !== seq) return;
      const message = err instanceof Error ? err.message : String(err);
      console.error('[catalog] group load failed:', err);
      set({ status: 'error', statusText: 'Failed to load group. Check connection.' });
      toast('error', `Failed to load satellite group: ${message}`);
    }
  },

  addToGlobe: (records) => {
    const loader = useLoader.getState();
    const dataset = loader.dataset;
    if (!dataset) {
      toast('warn', 'No network is loaded yet; the catalogue satellite cannot be placed.');
      return 0;
    }
    const existing = new Set(dataset.missions.map((m) => m.id));
    const added: Mission[] = [];
    const skipped: string[] = [];
    const nowMs = Date.now();
    for (const omm of records.slice(0, MAX_ADD_AT_ONCE)) {
      const noradId = ommNoradId(omm);
      if (!noradId || existing.has(catalogMissionId(noradId))) continue;
      const mission = catalogMissionFromOmm(omm, nextColor(), nowMs);
      if (!mission) {
        skipped.push(ommName(omm));
        continue;
      }
      added.push(mission);
      existing.add(mission.id);
    }
    if (added.length > 0) {
      // patchDataset resets the active set to every mission; keep the user's
      // what-if selection and make only the new catalogue satellites active.
      const active = loader.activeMissionIds;
      loader.patchDataset({ missions: [...dataset.missions, ...added] });
      useLoader.getState().setActiveMissionIds([...active, ...added.map((m) => m.id)]);
    }
    if (records.length > MAX_ADD_AT_ONCE) {
      toast('warn', `Only the first ${MAX_ADD_AT_ONCE} of ${records.length} satellites were added.`);
    }
    if (skipped.length > 0) {
      toast(
        'warn',
        `${skipped.length} satellite${skipped.length === 1 ? '' : 's'} had no usable orbit elements and ${skipped.length === 1 ? 'was' : 'were'} skipped (${skipped.slice(0, 3).join(', ')}).`,
      );
    }
    return added.length;
  },

  removeFromGlobe: (missionIds) => {
    const loader = useLoader.getState();
    const dataset = loader.dataset;
    if (!dataset || missionIds.length === 0) return;
    const gone = new Set(missionIds);
    const active = loader.activeMissionIds.filter((id) => !gone.has(id));
    loader.patchDataset({ missions: dataset.missions.filter((m) => !gone.has(m.id)) });
    useLoader.getState().setActiveMissionIds(active);
  },

  removeAll: () => {
    const dataset = useLoader.getState().dataset;
    get().removeFromGlobe(selectCatalogMissions(dataset?.missions).map((m) => m.id));
  },
}));

/** Opens the catalogue modal (bound by the shell to the C key and a strip button). */
export function openCatalog(): void {
  useCatalog.getState().openCatalog();
}

export function closeCatalog(): void {
  useCatalog.getState().closeCatalog();
}
